import { Editor } from "tinymce";

import {
  parseWildcardTag,
  REPLACE_WILDCARD_CLASS
} from "./replace-wildcards.utils";

/**
 * Editor-side support for Replace wildcards: highlight every {{replace...}}
 * token in red and let the clinician fill it in (free text, or a dropdown when
 * the token declares pipe-delimited choices). Wildcards are never bound to data
 * — see replace-wildcards.utils for the shared classifier used by the resolver.
 */

// Matches {{replace}}, {{replace.hint}}, {{replace.hint | a | b}} — case
// insensitive on the keyword. `replacement` etc. are excluded because the
// keyword must be followed by a dot or the closing braces.
const WILDCARD_TOKEN_REGEXP = /\{\{\s*replace(\.[^{}]*)?\s*\}\}/gi;
const TEMP_CONTROL_CLASS = "replace-wildcard-control";

const buildWildcardSpan = (editor: Editor, rawToken: string): HTMLElement => {
  const parsed = parseWildcardTag(rawToken);
  const title = parsed?.hint
    ? `Replace placeholder: ${parsed.hint}`
    : "Replace placeholder — fill in before completing";

  return editor.dom.create(
    "span",
    {
      class: REPLACE_WILDCARD_CLASS,
      "data-replace-wildcard": "1",
      contenteditable: "false",
      title
    },
    editor.dom.encode(rawToken)
  ) as HTMLElement;
};

const wrapWildcardsInTextNode = (editor: Editor, textNode: Text): void => {
  const text = textNode.nodeValue || "";
  WILDCARD_TOKEN_REGEXP.lastIndex = 0;

  if (!WILDCARD_TOKEN_REGEXP.test(text)) return;

  const doc = editor.getDoc();
  const fragment = doc.createDocumentFragment();
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  WILDCARD_TOKEN_REGEXP.lastIndex = 0;

  while ((match = WILDCARD_TOKEN_REGEXP.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    if (before) fragment.appendChild(doc.createTextNode(before));
    fragment.appendChild(buildWildcardSpan(editor, match[0]));
    lastIndex = match.index + match[0].length;
  }

  const after = text.slice(lastIndex);
  if (after) fragment.appendChild(doc.createTextNode(after));

  textNode.parentNode?.replaceChild(fragment, textNode);
};

let isDecorating = false;

const decorateWildcards = (editor: Editor): void => {
  if (isDecorating) return;
  const body = editor.getBody();
  if (!body) return;

  const walker = editor
    .getDoc()
    .createTreeWalker(body, NodeFilter.SHOW_TEXT, null);

  const targets: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    const value = textNode.nodeValue;
    if (!value || value.indexOf("{{") < 0) continue;
    const parentEl = textNode.parentElement;
    if (parentEl && parentEl.closest(`.${REPLACE_WILDCARD_CLASS}`)) continue;
    WILDCARD_TOKEN_REGEXP.lastIndex = 0;
    if (WILDCARD_TOKEN_REGEXP.test(value)) targets.push(textNode);
  }

  if (!targets.length) return;

  isDecorating = true;
  let bookmark: any;
  try {
    bookmark = editor.selection.getBookmark(2);
  } catch {
    bookmark = undefined;
  }
  try {
    targets.forEach((textNode) => wrapWildcardsInTextNode(editor, textNode));
  } finally {
    if (bookmark) {
      try {
        editor.selection.moveToBookmark(bookmark);
      } catch {}
    }
    isDecorating = false;
  }
};

const getWildcardElement = (node: HTMLElement | null): HTMLElement | null => {
  if (!node) return null;
  if (node.classList?.contains(REPLACE_WILDCARD_CLASS)) return node;
  return node.closest?.(`.${REPLACE_WILDCARD_CLASS}`) as HTMLElement | null;
};

const replaceNodeWithValue = (
  editor: Editor,
  node: HTMLElement,
  value: string
): void => {
  const clean = value.trim();
  if (!clean || !node.isConnected) return;

  editor.undoManager.transact(() => {
    const textNode = editor.getDoc().createTextNode(clean);
    node.replaceWith(textNode);
    try {
      editor.selection.setCursorLocation(textNode, textNode.length);
    } catch {}
  });
  editor.nodeChanged();
  editor.dispatch("change");
};

const restoreWildcard = (
  editor: Editor,
  control: HTMLElement,
  rawToken: string
) => {
  if (!control.isConnected) return;
  control.replaceWith(buildWildcardSpan(editor, rawToken));
};

const applyInlineControlStyles = (element: HTMLElement) => {
  element.style.fontFamily = "inherit";
  element.style.fontSize = "13px";
  element.style.padding = "2px 6px";
  element.style.border = "1px solid #d0d5dd";
  element.style.borderRadius = "4px";
  element.style.background = "#fff";
  element.style.color = "#101828";
  element.style.verticalAlign = "middle";
};

const buildSelectControl = (
  editor: Editor,
  node: HTMLElement,
  rawToken: string,
  options: string[],
  label: string
) => {
  const doc = editor.getDoc();
  const select = doc.createElement("select");
  select.className = TEMP_CONTROL_CLASS;
  select.setAttribute("aria-label", label || "Replace placeholder");
  select.size = Math.min(options.length + 1, 6);
  applyInlineControlStyles(select);

  const placeholder = doc.createElement("option");
  placeholder.value = "";
  placeholder.textContent = label || "Select value";
  placeholder.disabled = true;
  placeholder.selected = true;
  select.appendChild(placeholder);

  options.forEach((option) => {
    const item = doc.createElement("option");
    item.value = option;
    item.textContent = option;
    select.appendChild(item);
  });

  let committed = false;
  select.addEventListener("change", () => {
    committed = true;
    replaceNodeWithValue(editor, select, select.value);
  });
  select.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      restoreWildcard(editor, select, rawToken);
    }
  });
  select.addEventListener("blur", () => {
    setTimeout(() => {
      if (!committed) restoreWildcard(editor, select, rawToken);
    }, 0);
  });

  node.replaceWith(select);
  setTimeout(() => select.focus(), 0);
};

const buildInputControl = (
  editor: Editor,
  node: HTMLElement,
  rawToken: string,
  label: string
) => {
  const doc = editor.getDoc();
  const wrapper = doc.createElement("span");
  wrapper.className = TEMP_CONTROL_CLASS;
  wrapper.contentEditable = "true";
  wrapper.style.display = "inline-flex";
  wrapper.style.alignItems = "center";
  wrapper.style.gap = "4px";
  wrapper.style.verticalAlign = "middle";

  const input = doc.createElement("input");
  input.type = "text";
  input.placeholder = label || "Enter value";
  input.contentEditable = "true";
  input.style.minWidth = "160px";
  applyInlineControlStyles(input);

  const stopEditorEvent = (event: Event) => {
    event.stopPropagation();
  };

  input.addEventListener("focusin", stopEditorEvent);
  input.addEventListener("mousedown", stopEditorEvent);
  input.addEventListener("click", stopEditorEvent);
  input.addEventListener("beforeinput", stopEditorEvent);
  input.addEventListener("input", stopEditorEvent);
  input.addEventListener("keypress", stopEditorEvent);
  input.addEventListener("keyup", stopEditorEvent);

  const button = doc.createElement("button");
  button.type = "button";
  button.contentEditable = "false";
  button.textContent = "Insert";
  button.style.fontFamily = "inherit";
  button.style.fontSize = "13px";
  button.style.padding = "2px 8px";
  button.style.border = "none";
  button.style.borderRadius = "4px";
  button.style.background = "#054743";
  button.style.color = "#fff";
  button.style.cursor = "pointer";

  const submit = () => replaceNodeWithValue(editor, wrapper, input.value);
  input.addEventListener("keydown", (event) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      restoreWildcard(editor, wrapper, rawToken);
    }
  });
  button.addEventListener("mousedown", (event) => {
    event.preventDefault();
    event.stopPropagation();
  });
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    submit();
  });
  wrapper.addEventListener("focusout", () => {
    setTimeout(() => {
      const active = doc.activeElement;
      if (active && wrapper.contains(active)) return;
      restoreWildcard(editor, wrapper, rawToken);
    }, 0);
  });

  wrapper.appendChild(input);
  wrapper.appendChild(button);
  node.replaceWith(wrapper);
  setTimeout(() => input.focus(), 0);
};

const openReplaceWildcardControl = (
  editor: Editor,
  wildcardNode?: HTMLElement | null
): void => {
  const node = getWildcardElement(
    wildcardNode ?? (editor.selection.getNode() as HTMLElement | null)
  );
  if (!node) return;

  const parsed = parseWildcardTag(node.textContent || "");
  if (!parsed) return;

  const rawToken = node.textContent || "";
  if (parsed.options.length) {
    buildSelectControl(editor, node, rawToken, parsed.options, parsed.hint);
  } else {
    buildInputControl(editor, node, rawToken, parsed.hint);
  }
};

export const handleReplaceWildcards = (editor: Editor): void => {
  const scheduleDecorateWildcards = () => {
    setTimeout(() => decorateWildcards(editor), 0);
  };

  editor.on("mousedown", (event: any) => {
    const target = getWildcardElement(event?.target as HTMLElement | null);
    if (target) {
      event.preventDefault?.();
      event.stopPropagation?.();
      openReplaceWildcardControl(editor, target);
    }
  });

  editor.on("init", () => decorateWildcards(editor));
  editor.on("SetContent", () => decorateWildcards(editor));
  editor.on(
    "Input PastePostProcess Change Undo Redo",
    scheduleDecorateWildcards
  );
  editor.on("ExecCommand", (event: any) => {
    if (event?.command === "mceInsertContent") {
      scheduleDecorateWildcards();
    }
  });
};
